import { type FC, useContext } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { AuthContext } from "../hooks/authProvider/authProvider";
import AdminDashboardPageContainer from "../routes/admin/AdminDashboardPage/index";
import {
  ContactDetailPage,
  ContactFormPage,
  ContactsPage,
} from "../routes/admin/distribution/contacts";
import {
  AddContactsPage,
  GroupDetailPage,
  GroupFormPage,
  GroupsPage,
} from "../routes/admin/distribution/groups";
import { TenantDetailPage, TenantsPage } from "../routes/admin/tenants";
import { UserDetailPage, UsersPage } from "../routes/admin/users";
import { canAccessAdminPages } from "../utils/admin-utils";

/**
 * 管理者権限を持つユーザーのみアクセス可能なルートを保護し、
 * 管理者向けのルーティングも担当するコンポーネント
 * @return {JSX.Element} 管理者向けルート保護・ルーティングコンポーネント
 */
export const AdminRoute: FC = () => {
  const { currentUser, isLoadingUser } = useContext(AuthContext);

  // ローディング中またはcurrentUserが取得できていない場合は待機
  if (isLoadingUser || !currentUser) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="text-lg">管理者権限を確認中...</div>
      </div>
    );
  }

  // currentUserが取得できた後に管理者権限をチェック
  if (!canAccessAdminPages(currentUser)) {
    return <Navigate to="/" replace />;
  }

  // 管理者権限がある場合は管理者向けルーティングを表示
  return (
    <Routes>
      <Route path="/" element={<AdminDashboardPageContainer />} />
      <Route path="/tenants" element={<TenantsPage />} />
      <Route path="/tenants/:id" element={<TenantDetailPage />} />
      <Route path="/users" element={<UsersPage />} />
      <Route path="/users/:id" element={<UserDetailPage />} />
      {/* Distribution Groups */}
      <Route path="/distribution/groups" element={<GroupsPage />} />
      <Route path="/distribution/groups/new" element={<GroupFormPage />} />
      <Route path="/distribution/groups/:id" element={<GroupDetailPage />} />
      <Route path="/distribution/groups/:id/edit" element={<GroupFormPage />} />
      <Route
        path="/distribution/groups/:id/add-contacts"
        element={<AddContactsPage />}
      />
      {/* Distribution Contacts */}
      <Route path="/distribution/contacts" element={<ContactsPage />} />
      <Route path="/distribution/contacts/new" element={<ContactFormPage />} />
      <Route
        path="/distribution/contacts/:id"
        element={<ContactDetailPage />}
      />
      <Route
        path="/distribution/contacts/:id/edit"
        element={<ContactFormPage />}
      />
    </Routes>
  );
};

export default AdminRoute;
